import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { translations } from '../translations';
import apiService from '../services/apiService';
import WeightMeasurement from './WeightMeasurement';
import QoR15Questionnaire from './QoR15Questionnaire';
import QoR15ScoreDisplay from './QoR15ScoreDisplay';
import VideoFrame from './VideoFrame';

function Timeline({ language = 'en', patientData, onHeaderVisibilityChange }) {
  console.log('Timeline - Composant monté/remonté');
  
  // Restaurer l'état de restauration immédiatement au montage
  const savedHasRestored = localStorage.getItem(`timeline_has_restored_${patientData?.patientId}`);
  const initialHasRestored = savedHasRestored === 'true';
  console.log('Timeline - État initial de restauration:', initialHasRestored, 'savedHasRestored:', savedHasRestored);
  
  // Pré-initialiser la timeline à partir du cache pour éviter le flash au montage
  const getInitialTimeline = () => {
    try {
      if (!patientData || !patientData.patientId) return null;
      const raw = localStorage.getItem(`timeline_data_${patientData.patientId}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const age = Date.now() - parsed.timestamp;
      // Invalider si la startDate a changé côté patient
      if (parsed?.data?.patientInfo?.startDate && patientData.startDate && parsed.data.patientInfo.startDate !== patientData.startDate) {
        return null;
      }
      // Désactiver le cache pour le groupe Control afin de refléter la timeline simplifiée
      const cachedGroup = String(parsed?.data?.patientInfo?.group || '').toLowerCase();
      if (cachedGroup === 'control') {
        return null;
      }
      if (age < 5 * 60 * 1000 && parsed && parsed.data && parsed.data.timeline) {
        // Nettoyer les doublons même pour les données du cache
        return cleanTimelineData(parsed.data);
      }
    } catch (e) {}
    return null;
  };
  const [timelineData, setTimelineData] = useState(getInitialTimeline());
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [animatingPhase, setAnimatingPhase] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [hasRestored, setHasRestored] = useState(initialHasRestored);
  const hasRestoredRef = useRef(initialHasRestored);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(() => getInitialTimeline() ? false : true);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [showQoR15Modal, setShowQoR15Modal] = useState(false);
  const [showQoR15Results, setShowQoR15Results] = useState(false);
  const [qor15Score, setQor15Score] = useState(null);
  const [qor15Responses, setQor15Responses] = useState(null);
  const [activeVideo, setActiveVideo] = useState('intro1');
  const [fastenExpanded, setFastenExpanded] = useState(true);
  const [intervalExpanded, setIntervalExpanded] = useState(true);
  const t = translations[language] || translations.en;

  // Protection contre les remontages multiples
  const hasInitializedRef = useRef(false);

  // Masquer le header immédiatement au premier rendu si on revient d'une page jour/article
  const shouldHideHeaderRef = useRef(
    (typeof sessionStorage !== 'undefined' && (
      sessionStorage.getItem('timeline_from_day_page') === 'true' ||
      sessionStorage.getItem('timeline_from_article_page') === 'true'
    )) || false
  );

  // Informer le parent du besoin de masquer/afficher le header
  useEffect(() => {
    if (typeof onHeaderVisibilityChange === 'function') {
      onHeaderVisibilityChange(isRestoring || shouldHideHeaderRef.current);
    }
    return () => {
      if (typeof onHeaderVisibilityChange === 'function') {
        onHeaderVisibilityChange(false);
      }
    };
  }, [isRestoring, onHeaderVisibilityChange]);

  // Cache pour les données de la timeline
  const timelineCacheKey = `timeline_data_${patientData?.patientId}`;

  // Fonction pour tester le localStorage
  const testLocalStorage = () => {
    try {
      const testKey = 'timeline_test';
      const testValue = 'test_value';
      localStorage.setItem(testKey, testValue);
      const retrievedValue = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      
      if (retrievedValue === testValue) {
        console.log('LocalStorage test: SUCCESS');
        return true;
      } else {
        console.log('LocalStorage test: FAILED - Valeur incorrecte');
        return false;
      }
    } catch (error) {
      console.log('LocalStorage test: FAILED - Erreur:', error);
      return false;
    }
  };

  // Calculer le jour courant global selon la startDate du patient
  const today = new Date();
  const startDate = patientData ? new Date(patientData.startDate + 'T00:00:00.000Z') : null;
  
  // Fonction pour calculer la date de chaque phase
  const getPhaseDate = (phase) => {
    if (!startDate) return '';
    
    // Cas spécial: Operation utilise la date d'opération si fournie
    if (phase?.phaseId === 'operation') {
      const opDate = patientData?.operationDate
        ? new Date(patientData.operationDate + 'T00:00:00.000Z')
        : (() => { const d = new Date(startDate); d.setDate(startDate.getDate() + 21); return d; })();
      return opDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    
    // Calcul générique: prendre le plus petit dayNumber de la phase et l'appliquer à la règle
    // date du jour = startDate + (dayNumber + 21) jours
    const hasDays = Array.isArray(phase?.days) && phase.days.length > 0;
    if (hasDays) {
      const minDayNumber = phase.days.reduce((min, d) => (d.dayNumber < min ? d.dayNumber : min), phase.days[0].dayNumber);
      const offset = minDayNumber + 21;
      const phaseDate = new Date(startDate);
      phaseDate.setDate(startDate.getDate() + offset);
      return phaseDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    
    // Fallback: si aucune journée, afficher startDate
    return new Date(startDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  
  // Normaliser les dates à minuit pour comparer les jours calendaires
  const daysSinceStart = startDate ? (() => {
    const startDateNormalized = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.max(0, Math.floor((todayNormalized - startDateNormalized) / (1000 * 60 * 60 * 24)));
  })() : 0;

  // Déterminer la clé unique (phaseId-dayNumber) correspondant au jour courant
  const currentDayKey = (() => {
    if (!timelineData || !timelineData.timeline) return null;
    let counter = 0;
    let lastUnlockedKey = null;
    for (const phase of timelineData.timeline) {
      for (const d of phase.days) {
        if (d.unlocked) {
          counter += 1;
          lastUnlockedKey = `${phase.phaseId}-${d.dayNumber}`;
          // Le jour 0 (startDate) correspond au premier jour débloqué (counter = 1)
          if (counter === daysSinceStart + 1) {
            return `${phase.phaseId}-${d.dayNumber}`;
          }
        }
      }
    }
    // Si l'index dépasse, retourner le dernier jour débloqué
    return lastUnlockedKey;
  })();

  // Fonction pour trouver le jour actuel et sa phase
  const getCurrentDayAndPhase = () => {
    if (!timelineData || !timelineData.timeline || !currentDayKey) return null;
    
    for (const phase of timelineData.timeline) {
      for (const day of phase.days) {
        if (`${phase.phaseId}-${day.dayNumber}` === currentDayKey) {
          return { day, phase };
        }
      }
    }
    return null;
  };

  // Restaurer la position de scroll avant peinture pour éviter le flash
  useLayoutEffect(() => {
    if (!patientData?.patientId) return;
    const fromDay = sessionStorage.getItem('timeline_from_day_page') === 'true';
    const fromArticle = sessionStorage.getItem('timeline_from_article_page') === 'true';
    if (!fromDay && !fromArticle) return;
    const savedScrollPosition = localStorage.getItem(`timeline_scroll_position_${patientData.patientId}`);
    if (savedScrollPosition !== null) {
      const scrollPosition = parseInt(savedScrollPosition);
      if (!isNaN(scrollPosition) && scrollPosition >= 0) {
        try {
          window.scrollTo({ top: scrollPosition, behavior: 'instant' });
        } catch (_) {
          window.scrollTo(0, scrollPosition);
        }
      }
    }
  }, [patientData?.patientId]);

  useEffect(() => {
    console.log('Timeline - useEffect loadTimelineData, patientData:', patientData?.patientId);
    
    // Éviter les appels multiples
    if (hasInitializedRef.current) {
      console.log('Timeline - Déjà initialisé, skip');
      return;
    }
    
    const loadData = async () => {
      console.log('Timeline - loadTimelineData appelé');
      
      if (patientData && patientData.patientId) {
        hasInitializedRef.current = true;
        
        // Tester le localStorage
        const localStorageWorking = testLocalStorage();
        console.log('LocalStorage fonctionne:', localStorageWorking);
        
        try {
          // Déterminer si l'utilisateur revient d'une page de jour ou d'article
          const sessionFromDayPage = sessionStorage.getItem('timeline_from_day_page');
          const sessionFromArticlePage = sessionStorage.getItem('timeline_from_article_page');
          const isFromDayPage = sessionFromDayPage === 'true';
          const isFromArticlePage = sessionFromArticlePage === 'true';

          console.log('Timeline - Flags de navigation:', { isFromDayPage, isFromArticlePage });

          // Vérifier d'abord le cache
          const cachedData = localStorage.getItem(timelineCacheKey);
          if (cachedData) {
            try {
              const parsedData = JSON.parse(cachedData);
              const cacheAge = Date.now() - parsedData.timestamp;
              // Invalider si la startDate a changé côté patient
              const cachedStart = parsedData?.data?.patientInfo?.startDate;
              if (cachedStart && patientData.startDate && cachedStart !== patientData.startDate) {
                console.log('Cache invalidé (startDate modifiée)');
              } else if (cacheAge < 5 * 60 * 1000) {
                console.log('Utilisation du cache pour la timeline');
                setTimelineData(parsedData.data);
                setLoading(false);
                
                // Restaurer l'état seulement si l'utilisateur vient d'une page de jour ou d'article
                if (isFromDayPage || isFromArticlePage) {
                  // Nettoyer le flag après utilisation
                  sessionStorage.removeItem('timeline_from_day_page');
                  sessionStorage.removeItem('timeline_from_article_page');
                  console.log('Timeline - Flags sessionStorage nettoyés');
                  
                  const savedExpandedPhase = localStorage.getItem(`timeline_expanded_phase_${patientData.patientId}`);
                  console.log('Restauration - Phase sauvegardée:', savedExpandedPhase);
                  console.log('Restauration - Patient ID:', patientData.patientId);
                  console.log('Restauration - Nombre de phases:', parsedData.data.timeline.length);
                  
                  if (savedExpandedPhase !== null) {
                    const phaseIndex = parseInt(savedExpandedPhase);
                    console.log('Restauration - Index parsé:', phaseIndex, 'Nombre de phases:', parsedData.data.timeline.length);
                    console.log('Restauration - Index valide:', !isNaN(phaseIndex) && phaseIndex >= 0 && phaseIndex < parsedData.data.timeline.length);
                    
                    if (!isNaN(phaseIndex) && phaseIndex >= 0 && phaseIndex < parsedData.data.timeline.length) {
                      console.log('Restauration - Ouverture de la phase:', phaseIndex);
                      
                      // Restaurer l'état immédiatement sans animation
                      setIsRestoring(true);
                      setHasRestored(true);
                      hasRestoredRef.current = true;
                      localStorage.setItem(`timeline_has_restored_${patientData.patientId}`, 'true');
                      setExpandedPhase(phaseIndex);
                      
                      // Restaurer la position de scroll immédiatement avec un petit délai pour s'assurer que le DOM est prêt
                      const savedScrollPosition = localStorage.getItem(`timeline_scroll_position_${patientData.patientId}`);
                      console.log('Restauration - Position scroll sauvegardée:', savedScrollPosition);
                      
                      if (savedScrollPosition !== null) {
                        const scrollPosition = parseInt(savedScrollPosition);
                        console.log('Restauration - Position scroll parsée:', scrollPosition);
                        console.log('Restauration - Position scroll valide:', !isNaN(scrollPosition) && scrollPosition >= 0);
                        
                        if (!isNaN(scrollPosition) && scrollPosition >= 0) {
                          console.log('Restauration - Application de la position scroll:', scrollPosition);
                          // Appliquer avec un petit délai pour s'assurer que le DOM est prêt
                          setTimeout(() => {
                            try {
                              window.scrollTo({ top: scrollPosition, behavior: 'instant' });
                            } catch (_) {
                              window.scrollTo(0, scrollPosition);
                            }
                            // Désactiver le mode restauration après application
                            setTimeout(() => {
                              setIsRestoring(false);
                              shouldHideHeaderRef.current = false;
                            }, 0);
                          }, 0);
                        } else {
                          // Désactiver le mode restauration si position invalide
                          setIsRestoring(false);
                          shouldHideHeaderRef.current = false;
                        }
                      } else {
                        // Désactiver le mode restauration si pas de scroll sauvegardé
                        setIsRestoring(false);
                        shouldHideHeaderRef.current = false;
                      }
                    } else {
                      console.log('Restauration - ÉCHEC: Index de phase invalide');
                    }
                  } else {
                    console.log('Restauration - ÉCHEC: Aucune phase sauvegardée trouvée');
                  }
                } else {
                  console.log('Timeline - Pas de restauration car pas de navigation depuis une page de jour ou article');
                }
                return;
              }
            } catch (e) {
              console.log('Cache invalide, rechargement nécessaire');
            }
          }

          // Éviter le flash: ne montrer le spinner que si aucune donnée en cache
          if (!timelineData) {
            setLoading(true);
          }
          const response = await apiService.getPatientTimeline(patientData.patientId);
          if (response.success) {
              // Nettoyer les doublons avant de traiter les données
              const cleanedResponse = cleanTimelineData(response);
              
              // Si groupe Control, nettoyer l'ancien cache pour éviter l’affichage de l’ancienne structure
              try {
                const cachedRaw = localStorage.getItem(timelineCacheKey);
                if (cachedRaw) {
                  const cachedParsed = JSON.parse(cachedRaw);
                  const cachedGroup = String(cachedParsed?.data?.patientInfo?.group || '').toLowerCase();
                  if (cachedGroup === 'control') {
                    localStorage.removeItem(timelineCacheKey);
                  }
                }
              } catch (_) {}

              // Mettre en cache les données nettoyées
              const cacheData = {
                data: cleanedResponse,
                timestamp: Date.now()
              };
              localStorage.setItem(timelineCacheKey, JSON.stringify(cacheData));
              
            setTimelineData(cleanedResponse);
              setLoading(false);
              
              // Restaurer l'état seulement si l'utilisateur vient d'une page de jour ou d'article
              if (isFromDayPage || isFromArticlePage) {
                // Nettoyer le flag après utilisation
                sessionStorage.removeItem('timeline_from_day_page');
                sessionStorage.removeItem('timeline_from_article_page');
                console.log('Timeline - Flags sessionStorage nettoyés (API)');
                
                const savedExpandedPhase = localStorage.getItem(`timeline_expanded_phase_${patientData.patientId}`);
                console.log('Restauration - Phase sauvegardée:', savedExpandedPhase);
                console.log('Restauration - Patient ID:', patientData.patientId);
                console.log('Restauration - Nombre de phases:', response.timeline.length);
                if (savedExpandedPhase !== null) {
                  const phaseIndex = parseInt(savedExpandedPhase);
                  console.log('Restauration - Index parsé:', phaseIndex, 'Nombre de phases:', response.timeline.length);
                  console.log('Restauration - Index valide:', !isNaN(phaseIndex) && phaseIndex >= 0 && phaseIndex < response.timeline.length);
                  if (!isNaN(phaseIndex) && phaseIndex >= 0 && phaseIndex < response.timeline.length) {
                    console.log('Restauration - Ouverture de la phase:', phaseIndex);
                    
                    // Restaurer l'état immédiatement sans animation
                    setIsRestoring(true);
                    setHasRestored(true);
                    hasRestoredRef.current = true;
                    localStorage.setItem(`timeline_has_restored_${patientData.patientId}`, 'true');
                    setExpandedPhase(phaseIndex);
                    
                    // Restaurer la position de scroll immédiatement
                    const savedScrollPosition = localStorage.getItem(`timeline_scroll_position_${patientData.patientId}`);
                    console.log('Restauration (API) - Position scroll sauvegardée:', savedScrollPosition);
                    
                    if (savedScrollPosition !== null) {
                      const scrollPosition = parseInt(savedScrollPosition);
                      console.log('Restauration (API) - Position scroll parsée:', scrollPosition);
                      console.log('Restauration (API) - Position scroll valide:', !isNaN(scrollPosition) && scrollPosition >= 0);
                      
                      if (!isNaN(scrollPosition) && scrollPosition >= 0) {
                        console.log('Restauration (API) - Application de la position scroll:', scrollPosition);
                        // Appliquer avec un petit délai pour s'assurer que le DOM est prêt
                        setTimeout(() => {
                          try {
                            window.scrollTo({ top: scrollPosition, behavior: 'instant' });
                          } catch (_) {
                            window.scrollTo(0, scrollPosition);
                          }
                          // Désactiver le mode restauration après application
                          setTimeout(() => {
                            setIsRestoring(false);
                            shouldHideHeaderRef.current = false;
                          }, 0);
                        }, 0);
                      } else {
                        setIsRestoring(false);
                        shouldHideHeaderRef.current = false;
                      }
                    } else {
                      setIsRestoring(false);
                      shouldHideHeaderRef.current = false;
                    }
                  } else {
                    console.log('Restauration (API) - ÉCHEC: Index de phase invalide');
                  }
                } else {
                  console.log('Restauration (API) - ÉCHEC: Aucune phase sauvegardée trouvée');
                }
              } else {
                console.log('Timeline - Pas de restauration car pas de navigation depuis une page de jour ou article (API)');
            }
          }
        } catch (error) {
          console.error('Erreur chargement timeline:', error);
          setLoading(false);
        }
      }
    };
    
    loadData();
  }, [patientData?.patientId]); // Dépendance simplifiée

  const getPhaseTranslation = (phaseId) => {
    return t.timeline?.phases?.[phaseId] || { title: phaseId, description: '' };
  };

  const getDayTranslation = (phaseId, dayNumber) => {
    // Essayer d'abord dayTranslations, puis days pour la compatibilité
    return t.timeline?.dayTranslations?.[phaseId]?.[dayNumber] || 
           t.timeline?.days?.[phaseId]?.[dayNumber] || 
           { title: language === 'de' ? `Tag ${dayNumber}` : `Day ${dayNumber}`, description: '' };
  };

  // Fonction utilitaire pour nettoyer les doublons dans les tâches
  const removeDuplicateTasks = (tasks) => {
    if (!Array.isArray(tasks)) return tasks;
    
    const seen = new Set();
    return tasks.filter(task => {
      if (seen.has(task.taskId)) {
        return false;
      }
      seen.add(task.taskId);
      return true;
    });
  };

  // Fonction pour nettoyer les doublons dans toute la timeline
  const cleanTimelineData = (timeline) => {
    if (!timeline || !timeline.timeline) return timeline;
    
    const cleanedTimeline = {
      ...timeline,
      timeline: timeline.timeline.map(phase => ({
        ...phase,
        days: phase.days ? phase.days.map(day => ({
          ...day,
          tasks: removeDuplicateTasks(day.tasks)
        })) : phase.days
      }))
    };
    
    return cleanedTimeline;
  };

  const getDayStatusColor = (day) => {
    if (day.tasks && day.tasks.length > 0) {
      const completedTasks = day.tasks.filter(task => task.completed).length;
      const totalTasks = day.tasks.length;
      if (completedTasks === totalTasks) {
        return '#10b981'; // Green - all tasks completed
      } else if (completedTasks > 0) {
        return '#f59e0b'; // Orange - partially completed
      }
    }
    
    if (day.unlocked) {
      return '#3b82f6'; // Blue - accessible
    } else {
      return '#9ca3af'; // Gray - locked
    }
  };

  // Fonction pour nettoyer le cache de la timeline
  const clearTimelineCache = useCallback(() => {
    if (patientData?.patientId) {
      localStorage.removeItem(timelineCacheKey);
    }
  }, [patientData?.patientId, timelineCacheKey]);

  const handlePhaseClick = (index) => {
    const phase = timelineData.timeline[index];
    if (phase.progress.unlocked > 0) {
      // Réinitialiser les flags de restauration quand l'utilisateur interagit
      setIsRestoring(false);
      setHasRestored(false); // Réinitialiser l'état permanent
      hasRestoredRef.current = false; // Réinitialiser la référence
      
      // Nettoyer l'état de restauration du localStorage
      localStorage.removeItem(`timeline_has_restored_${patientData?.patientId}`);
      
      let newExpandedPhase;
      if (expandedPhase === index) {
        // Fermer la phase seulement si on n'est pas en restauration
        if (!isRestoring && !hasRestoredRef.current) {
        newExpandedPhase = null;
        setExpandedPhase(null);
        setAnimatingPhase(null);
          
          // Nettoyer le localStorage quand on ferme une phase
          localStorage.removeItem(`timeline_expanded_phase_${patientData?.patientId}`);
          localStorage.removeItem(`timeline_scroll_position_${patientData?.patientId}`);
          localStorage.removeItem(`timeline_has_restored_${patientData?.patientId}`);
          
          // Réinitialiser le flag d'initialisation pour permettre un nouveau chargement
          hasInitializedRef.current = false;
        } else {
          console.log('Timeline - Tentative de fermeture de phase pendant la restauration, ignorée');
        }
      } else {
        // Ouvrir la phase avec animation seulement si pas en restauration
        newExpandedPhase = index;
        setExpandedPhase(index);
        
        // Ne déclencher l'animation que si on n'est pas en restauration et qu'aucune restauration n'a eu lieu
        if (!isRestoring && !hasRestoredRef.current) {
        setAnimatingPhase(index);
        
        // Retirer la classe d'animation après l'animation
        setTimeout(() => {
          setAnimatingPhase(null);
        }, 600);
      }
      
        // Sauvegarder l'état dans le localStorage
        localStorage.setItem(`timeline_expanded_phase_${patientData?.patientId}`, index);
      }
    }
  };

  const handleDayClick = (phaseIndex, day) => {
    if (day && day.unlocked) {
      const phaseId = timelineData.timeline[phaseIndex].phaseId;
      
      // Sauvegarder l'état de la phase ouverte et la position de scroll
      console.log('Sauvegarde - Phase ouverte:', expandedPhase, 'Phase cliquée:', phaseIndex);
      console.log('Sauvegarde - Position scroll:', window.pageYOffset);
      console.log('Sauvegarde - Patient ID:', patientData?.patientId);
      console.log('Sauvegarde - expandedPhase type:', typeof expandedPhase, 'valeur:', expandedPhase);
      
      // Vérifier que expandedPhase est valide
      if (expandedPhase !== null && expandedPhase !== undefined) {
        localStorage.setItem(`timeline_expanded_phase_${patientData?.patientId}`, expandedPhase);
        localStorage.setItem(`timeline_scroll_position_${patientData?.patientId}`, window.pageYOffset);
        
        // Vérifier que la sauvegarde a fonctionné
        const savedPhase = localStorage.getItem(`timeline_expanded_phase_${patientData?.patientId}`);
        const savedScroll = localStorage.getItem(`timeline_scroll_position_${patientData?.patientId}`);
        console.log('Sauvegarde - Vérification - Phase sauvegardée:', savedPhase, 'Scroll sauvegardé:', savedScroll);
      } else {
        console.log('Sauvegarde - ERREUR: expandedPhase est null ou undefined');
      }
      
      // Marquer dans sessionStorage que l'utilisateur va vers une page de jour
      sessionStorage.setItem('timeline_from_day_page', 'true');
      console.log('Timeline - sessionStorage timeline_from_day_page défini à true');
      console.log('Timeline - sessionStorage disponible dans handleDayClick:', typeof sessionStorage !== 'undefined');
      console.log('Timeline - Vérification après setItem:', sessionStorage.getItem('timeline_from_day_page'));
      
      navigate(`/day/${phaseId}/${day.dayNumber}`, {
        state: { 
          day, 
          phaseId, 
          timelineData
        }
      });
    }
  };

  // Fonction pour gérer la navigation vers l'article
  const handleArticleClick = () => {
    // Sauvegarder l'état de la phase ouverte et la position de scroll
    console.log('Sauvegarde Article - Phase ouverte:', expandedPhase);
    console.log('Sauvegarde Article - Position scroll:', window.pageYOffset);
    console.log('Sauvegarde Article - Patient ID:', patientData?.patientId);
    
    // Vérifier que expandedPhase est valide
    if (expandedPhase !== null && expandedPhase !== undefined) {
      localStorage.setItem(`timeline_expanded_phase_${patientData?.patientId}`, expandedPhase);
      localStorage.setItem(`timeline_scroll_position_${patientData?.patientId}`, window.pageYOffset);
      
      // Vérifier que la sauvegarde a fonctionné
      const savedPhase = localStorage.getItem(`timeline_expanded_phase_${patientData?.patientId}`);
      const savedScroll = localStorage.getItem(`timeline_scroll_position_${patientData?.patientId}`);
      console.log('Sauvegarde Article - Vérification - Phase sauvegardée:', savedPhase, 'Scroll sauvegardé:', savedScroll);
    } else {
      console.log('Sauvegarde Article - ERREUR: expandedPhase est null ou undefined');
    }
    
    // Marquer dans sessionStorage que l'utilisateur va vers la page article
    sessionStorage.setItem('timeline_from_article_page', 'true');
    console.log('Timeline - sessionStorage timeline_from_article_page défini à true');
    console.log('Timeline - sessionStorage disponible dans handleArticleClick:', typeof sessionStorage !== 'undefined');
    console.log('Timeline - Vérification après setItem:', sessionStorage.getItem('timeline_from_article_page'));
    
    navigate('/article');
  };

  const getDayProgress = (day) => {
    if (!day.tasks || day.tasks.length === 0) return 0;
    const completedTasks = day.tasks.filter(task => task.completed).length;
    return (completedTasks / day.tasks.length) * 100;
  };

  // Fonction pour obtenir l'URL de la vidéo active
  const getVideoUrl = () => {
    switch (activeVideo) {
      case 'intro1':
        return 'https://www.youtube.com/embed/NebLbtwm0ec?rel=0&modestbranding=1&hl=de';
      case 'intro2':
        return 'https://www.youtube.com/embed/7w4MlXzxYao?rel=0&modestbranding=1&hl=de';
      case 'tipps':
        return 'https://www.youtube.com/embed/Ci6zGv1dMPE?rel=0&modestbranding=1&hl=de';
      default:
        return 'https://www.youtube.com/embed/NebLbtwm0ec?rel=0&modestbranding=1&hl=de';
    }
  };

  // Fonction pour déterminer si une phase est passée
  const isPhasePast = (phase) => {
    if (!phase.days || phase.days.length === 0) return false;
    
    // Une phase est considérée comme passée si tous ses jours sont déverrouillés
    // et qu'il y a des tâches incomplètes
    const allDaysUnlocked = phase.days.every(day => day.unlocked);
    const hasIncompleteTasks = phase.progress.completed < phase.progress.total;
    
    return allDaysUnlocked && hasIncompleteTasks;
  };

  if (loading) {
    return (
      <div className="timeline-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!timelineData || !timelineData.timeline) {
    return (
      <div className="timeline-container">
        <div className="error-message">No timeline data available</div>
      </div>
    );
  }

  const currentDayAndPhase = getCurrentDayAndPhase();

  return (
      <div className="timeline-container">
      {/* Section Today tasks */}
      {currentDayAndPhase && (
        <div className="today-tasks-section">
          <h2 className="today-tasks-title">Heutige Aufgaben</h2>
          <div className="today-tasks-container">
            {currentDayAndPhase.phase.phaseId === 'operation' ? (
              <div className="today-operation-message">
                <div className="operation-icon">🏥</div>
                <div className="operation-content">
                  <h3>Ihr Operationstag</h3>
                  <p>
                    Sie haben heute Ihre Operation. Es gibt keine speziellen Aufgaben für heute. 
                    Entspannen Sie sich und organisieren Sie Ihre Ankunft in der Klinik gut. 
                    Wir wünschen Ihnen alles Gute für den Eingriff!
                  </p>
                </div>
              </div>
            ) : (
              <div 
                className="today-day-card day-card"
                data-day-key={`${currentDayAndPhase.phase.phaseId}-${currentDayAndPhase.day.dayNumber}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const phaseIndex = timelineData.timeline.findIndex(p => p.phaseId === currentDayAndPhase.phase.phaseId);
                  if (phaseIndex !== -1) {
                    handleDayClick(phaseIndex, currentDayAndPhase.day);
                  }
                }}
              >
                <div className="day-number">
                  {getDayTranslation(currentDayAndPhase.phase.phaseId, currentDayAndPhase.day.dayNumber).title}
                </div>
                
                {currentDayAndPhase.day.tasks && currentDayAndPhase.day.tasks.length > 0 && (
                  <div className="day-progress-bar">
                    <div className="progress-bar-bg">
                      <div 
                        className="progress-bar-fill"
                        style={{
                          width: `${(currentDayAndPhase.day.tasks.filter(task => task.completed).length / currentDayAndPhase.day.tasks.length) * 100}%`
                        }}
                      ></div>
                    </div>
                    <div className="progress-text">
                      {currentDayAndPhase.day.tasks.filter(task => task.completed).length}/{currentDayAndPhase.day.tasks.length}
                    </div>
                  </div>
                )}
                
                {currentDayAndPhase.day.tasks && currentDayAndPhase.day.tasks.length > 0 && 
                 currentDayAndPhase.day.tasks.every(task => task.completed) && (
                  <div className="checkmark">✓</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="timeline">
        {timelineData.timeline.map((phase, index) => {
          const isExpanded = expandedPhase === index;
          const isUnlocked = phase.progress.unlocked > 0;

          return (
            <div key={phase.phaseId} className={`timeline-item ${isUnlocked ? 'unlocked' : 'locked'}`}>
              <div className={`timeline-dot ${isUnlocked ? 'unlocked' : 'locked'}`}></div>
              
            <div
                className={`timeline-content ${!isUnlocked ? 'locked' : ''}`}
              onClick={() => handlePhaseClick(index)}
            >
                <div className={`timeline-date ${!isUnlocked ? 'locked' : ''}`}>
                  {!isUnlocked && <span className="lock-icon">🔒</span>}
                  {getPhaseDate(phase)}
                  {isUnlocked && (
                    <span className={`expand-arrow ${isExpanded ? 'expanded' : ''}`}>
                      ▼
                    </span>
                  )}
                </div>
                
                <h3 className={`timeline-title-item ${!isUnlocked ? 'locked' : ''}`}>
                  {phase.phaseId === 'final-step' && (
                    <img src="/images/flag.png" alt="Final" className="phase-icon" />
                  )}
                  {getPhaseTranslation(phase.phaseId).title}
                </h3>
                
                <p className={`timeline-description ${!isUnlocked ? 'locked' : ''}`}>
                  {getPhaseTranslation(phase.phaseId).description}
                </p>
                
                {/* Contenu étendu de la phase */}
                {isExpanded && isUnlocked && (
                  <div 
                    className={`phase-expanded ${!isRestoring && !hasRestoredRef.current && !hasRestored && animatingPhase === index ? 'animate' : ''}`}
                    data-phase-id={phase.phaseId}
                  >


                    {/* Article pour les phases Intervallfasten */}
                    {(phase.phaseId === 'intermittent-fasting' || phase.phaseId === 'intermittent') && (
                      <div className="phase-article-section">
                        <div className="article-content">
                          <div className="article-header">
                            <img src="/images/article-icon.png" alt="Article" className="article-icon" />
                            <h4>Intervallfasten Artikel</h4>
                </div>
                          <p>Hier finden Sie wichtige Informationen zum Intervallfasten...</p>
                          <button 
                            className="article-read-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArticleClick();
                            }}
                          >
                            Artikel lesen
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Liste des jours avec sections déroulables pour Buchinger */}
                    {phase.days && (
                      <div className="days-container-wrapper">
                        {(phase.phaseId === 'buchinger-fasten-1' || phase.phaseId === 'buchinger-fasten-2') ? (
                          <div>
                            {/* Section Fasten */}
                            <div className="fasting-subsection">
                              <div 
                                className="subsection-header"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFastenExpanded(!fastenExpanded);
                                }}
                              >
                                <h4 className="subsection-title">Fasten</h4>
                                <span className={`subsection-arrow ${fastenExpanded ? 'expanded' : ''}`}>▼</span>
                              </div>
                              <div className={`subsection-content ${fastenExpanded ? 'expanded' : 'collapsed'}`}>
                                {/* Vidéos d'introduction pour la phase Buchinger Fasten - 1 */}
                                {phase.phaseId === 'buchinger-fasten-1' && (
                  <div className="phase-video-section">
                                    <h3 className="video-section-title">Fasting Box - Einführungsvideos</h3>
                    <VideoFrame
                      videoUrl={getVideoUrl()}
                      title="Buchinger Fasten Video"
                      language={language}
                      showTitle={false}
                    />
                    <div className="video-nav">
                      <button 
                        className={`video-nav-btn ${activeVideo === 'intro1' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveVideo('intro1');
                        }}
                      >
                        Einführung
                      </button>
                      <button 
                        className={`video-nav-btn ${activeVideo === 'intro2' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveVideo('intro2');
                        }}
                      >
                        Tipps 1/2
                      </button>
                      <button 
                        className={`video-nav-btn ${activeVideo === 'tipps' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveVideo('tipps');
                        }}
                      >
                        Tipps 2/2
                      </button>
                    </div>
                  </div>
                )}
                                <div className="sub-phase-section" data-sub-phase="fasting">
                                  {phase.days.slice(0, 5).map((day, dayIndex) => {
                                const isCurrentDay = day.unlocked && `${phase.phaseId}-${day.dayNumber}` === currentDayKey;
                                const isCompleted = day.tasks && day.tasks.length > 0 && day.tasks.every(task => task.completed);
                                
                                return (
                                  <div 
                                    key={`${phase.phaseId}-${day.dayNumber}`}
                                    data-day-key={`${phase.phaseId}-${day.dayNumber}`}
                                    className={`day-card ${day.unlocked ? (isCompleted ? 'completed' : '') : 'locked'} ${isCurrentDay ? 'current-day' : ''}` }
                        onClick={(e) => {
                          e.stopPropagation();
                                      handleDayClick(index, day);
                                    }}
                                  >
                                    {isCompleted && <div className="checkmark">✓</div>}
                                    {!day.unlocked && <div className="lock-small">🔒</div>}
                                    
                                    <div className="day-number">
                                      {getDayTranslation(phase.phaseId, day.dayNumber).title}
                                    </div>
                                    
                                    {day.unlocked && day.tasks && day.tasks.length > 0 && (
                                      <div className="day-progress-bar">
                                        <div className="progress-bar-bg">
                                          <div 
                                            className="progress-bar-fill"
                                            style={{
                                              width: `${(day.tasks.filter(task => task.completed).length / day.tasks.length) * 100}%`
                                            }}
                                          ></div>
                                        </div>
                                        <div className="progress-text">
                                          {day.tasks.filter(task => task.completed).length}/{day.tasks.length}
                    </div>
                  </div>
                )}
                                  </div>
                                    );
                                  })}
                                </div>
              </div>
            </div>

                            {/* Section Intervallfasten */}
                            <div className="fasting-subsection">
                              <div 
                                className="subsection-header"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIntervalExpanded(!intervalExpanded);
                                }}
                              >
                                <h4 className="subsection-title">Intervallfasten 14/10</h4>
                                <span className={`subsection-arrow ${intervalExpanded ? 'expanded' : ''}`}>▼</span>
                              </div>
                              <div className={`subsection-content ${intervalExpanded ? 'expanded' : 'collapsed'}`}>
                                <div className="sub-phase-section" data-sub-phase="intermittent">
                                  {phase.days.slice(5).map((day, dayIndex) => {
                   const isCurrentDay = day.unlocked && `${phase.phaseId}-${day.dayNumber}` === currentDayKey;
                                const isCompleted = day.tasks && day.tasks.length > 0 && day.tasks.every(task => task.completed);
                                
                   return (
                   <div 
                     key={`${phase.phaseId}-${day.dayNumber}`} 
                     data-day-key={`${phase.phaseId}-${day.dayNumber}`}
                                    className={`day-card ${day.unlocked ? (isCompleted ? 'completed' : '') : 'locked'} ${isCurrentDay ? 'current-day' : ''}` }
                     onClick={(e) => {
                       e.stopPropagation();
                       handleDayClick(index, day);
                     }}
                   >
                                    {isCompleted && <div className="checkmark">✓</div>}
                                    {!day.unlocked && <div className="lock-small">🔒</div>}
                                    
                                    <div className="day-number">
                          {getDayTranslation(phase.phaseId, day.dayNumber).title}
                                    </div>
                                    
                        {day.unlocked && day.tasks && day.tasks.length > 0 && (
                                      <div className="day-progress-bar">
                                        <div className="progress-bar-bg">
                                          <div 
                                            className="progress-bar-fill"
                                            style={{
                                              width: `${(day.tasks.filter(task => task.completed).length / day.tasks.length) * 100}%`
                                            }}
                                          ></div>
                                        </div>
                                        <div className="progress-text">
                                          {day.tasks.filter(task => task.completed).length}/{day.tasks.length}
                            </div>
                          </div>
                        )}
                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="days-container">
                            {/* Affichage normal pour les autres phases */}
                            {phase.days.map((day, dayIndex) => {
                              const isCurrentDay = day.unlocked && `${phase.phaseId}-${day.dayNumber}` === currentDayKey;
                              const isCompleted = day.tasks && day.tasks.length > 0 && day.tasks.every(task => task.completed);
                              
                              return (
                                <div 
                                  key={`${phase.phaseId}-${day.dayNumber}`}
                                  data-day-key={`${phase.phaseId}-${day.dayNumber}`}
                                  className={`day-card ${day.unlocked ? (isCompleted ? 'completed' : '') : 'locked'} ${isCurrentDay ? 'current-day' : ''}` }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDayClick(index, day);
                                  }}
                                >
                                  {isCompleted && <div className="checkmark">✓</div>}
                                  {!day.unlocked && <div className="lock-small">🔒</div>}
                                  
                                  <div className="day-number">
                                    {getDayTranslation(phase.phaseId, day.dayNumber).title}
                    </div>
                                  
                                  {day.unlocked && day.tasks && day.tasks.length > 0 && (
                                    <div className="day-progress-bar">
                                      <div className="progress-bar-bg">
                                        <div 
                                          className="progress-bar-fill"
                                          style={{
                                            width: `${(day.tasks.filter(task => task.completed).length / day.tasks.length) * 100}%`
                                          }}
                                        ></div>
                                      </div>
                                      <div className="progress-text">
                                        {day.tasks.filter(task => task.completed).length}/{day.tasks.length}
                                      </div>
                                    </div>
                                  )}
                  </div>
                );
                })}
              </div>
            )}
                      </div>
                    )}
                  </div>
                )}
              </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

export default Timeline; 